'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Controlled text input whose "committed" value drives downstream work.
 *
 * - `change` updates the visible value and commits it after `delay` ms (typing).
 * - `commitNow` updates and commits immediately, cancelling any pending debounce (explicit picks,
 *   e.g. clicking a line or suggestion).
 * - `hasSettled` is true when the visible value equals the committed value.
 */
export const useDebouncedCommit = (initialValue: string, delay: number) => {
   const [value, setValue] = useState(initialValue);
   const [committedValue, setCommittedValue] = useState(initialValue);
   const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const clearPending = useCallback(() => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
   }, []);

   const change = useCallback(
      (next: string) => {
         setValue(next);
         clearPending();
         timeoutRef.current = setTimeout(() => setCommittedValue(next), delay);
      },
      [delay, clearPending]
   );

   const commitNow = useCallback(
      (next: string) => {
         clearPending();
         setValue(next);
         setCommittedValue(next);
      },
      [clearPending]
   );

   // Cancel any pending debounce when the hook unmounts (avoids committing after unmount).
   useEffect(
      () => () => {
         if (timeoutRef.current) clearTimeout(timeoutRef.current);
      },
      []
   );

   return { value, committedValue, hasSettled: value === committedValue, change, commitNow };
};
